import React from 'react';
import StatusBadge from '../../common/StatusBadge';
import RankMovementLabel from '../../leaderboard/RankMovementLabel';
import { getPlayerNameColor } from '../../../utils/playerUtils';
import { getRankTier, RANK_TIER_CLASSES } from '../../../utils/leaderboardRankTiers';
import {
  demoSession,
  demoPerformance,
  demoClub,
  demoLeaderboard,
} from './landingDemoData';

const MockChrome = ({ title = 'Shuttlr', children }) => (
  <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-border-medium bg-surface shadow-brand-md">
    <div className="flex shrink-0 items-center gap-2 bg-[#0e1624] px-3 py-2">
      <img src="/latest_icon.png" alt="" className="h-5 w-5 rounded" />
      <span className="text-xs font-semibold text-white">{title}</span>
      <span className="hidden sm:inline text-[10px] text-white/50">Home · Clubs · Sessions</span>
    </div>
    <div className="flex min-h-0 flex-1 flex-col">{children}</div>
  </div>
);

const SessionPreview = () => (
  <MockChrome>
    <div className="flex flex-1 flex-col p-3">
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
        Featured session
      </p>
      <div className="flex flex-1 flex-col overflow-hidden rounded-xl border border-border-light">
        <img
          src="/default-club-banner.png"
          alt=""
          className="h-20 w-full object-cover sm:h-24"
        />
        <div className="flex flex-1 items-start justify-between gap-2 p-3">
          <div>
            <p className="text-sm font-semibold text-text-primary">{demoSession.venue}</p>
            <p className="text-xs text-text-secondary">{demoSession.subtitle}</p>
            <p className="mt-1 text-xs text-text-muted">{demoSession.when}</p>
          </div>
          <StatusBadge variant="success" size="small">
            {demoSession.spotsLeft} spots left
          </StatusBadge>
        </div>
      </div>
    </div>
  </MockChrome>
);

const PerformancePreview = () => (
  <div className="rounded-2xl border border-border-medium bg-surface p-4 shadow-brand-md">
    <p className="text-[10px] font-semibold uppercase tracking-wider text-text-muted mb-3">
      Your performance
    </p>
    <div className="flex items-center gap-4">
      <div className="relative flex h-16 w-16 shrink-0 items-center justify-center">
        <svg className="h-16 w-16 -rotate-90" viewBox="0 0 36 36" aria-hidden="true">
          <circle cx="18" cy="18" r="15" fill="none" className="stroke-border-medium" strokeWidth="3" />
          <circle
            cx="18"
            cy="18"
            r="15"
            fill="none"
            className="stroke-success"
            strokeWidth="3"
            strokeDasharray={`${demoPerformance.winRate} 100`}
            strokeLinecap="round"
          />
        </svg>
        <span className="absolute text-xs font-bold text-text-primary">{demoPerformance.winRate}%</span>
      </div>
      <div className="min-w-0">
        <p className="text-xs text-text-muted">Win rate</p>
        <p className="text-sm font-semibold text-text-primary">
          Elo {demoPerformance.elo.toLocaleString()}
          <span className="ml-1 text-success-dark">+{demoPerformance.eloDelta}</span>
        </p>
        <p className="text-xs text-text-secondary">Games played {demoPerformance.gamesPlayed}</p>
      </div>
    </div>
  </div>
);

const ClubPreview = () => (
  <div className="rounded-2xl border border-border-medium bg-surface p-4 shadow-brand-md">
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-light text-sm font-bold text-primary">
        RB
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-text-primary">{demoClub.name}</p>
        <p className="text-xs text-text-muted">{demoClub.activity}</p>
      </div>
    </div>
    <div className="mt-3 flex items-center justify-between">
      <div className="flex -space-x-2" aria-hidden="true">
        {['AC', 'SK', 'JL', 'PN'].map((initials) => (
          <span
            key={initials}
            className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-surface bg-secondary text-[10px] font-semibold text-text-inverse"
          >
            {initials}
          </span>
        ))}
      </div>
      <span className="text-xs font-medium text-primary">View club</span>
    </div>
  </div>
);

const LeaderboardPreview = () => (
  <MockChrome>
    <div className="flex flex-1 flex-col p-3">
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-text-muted">
        Club Leaderboard
      </p>
      <ol className="flex flex-1 flex-col justify-between gap-1">
        {demoLeaderboard.map((player) => {
          const tier = getRankTier(player.rank, 'preview');
          const rankClass = RANK_TIER_CLASSES[tier]?.rank
            || 'border-border-medium text-text-primary';
          return (
            <li key={player.rank} className="flex items-center gap-2 text-xs">
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-semibold ${rankClass}`}
              >
                {player.rank}
              </span>
              <span className={`min-w-0 flex-1 truncate font-medium ${getPlayerNameColor(player.gender)}`}>
                {player.name}
              </span>
              <RankMovementLabel rankDelta={player.rankDelta} size="xs" />
              <span className="w-10 shrink-0 text-right tabular-nums text-text-secondary">
                {player.elo.toLocaleString()}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  </MockChrome>
);

const LandingHeroCollage = () => (
  <div className="relative mx-auto w-full max-w-xl lg:max-w-none" aria-hidden="true">
    <div className="pointer-events-none absolute -right-8 -top-10 h-40 w-40 rounded-full bg-primary/10 blur-2xl" />
    <div className="grid grid-cols-2 items-stretch gap-3 sm:gap-4">
      <div className="col-span-2 h-full sm:col-span-1">
        <SessionPreview />
      </div>
      <div className="hidden h-full sm:block">
        <LeaderboardPreview />
      </div>
      <PerformancePreview />
      <ClubPreview />
    </div>
  </div>
);

export default LandingHeroCollage;
